import { useRef, useState } from "react";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { type ImportReport, importPractitionersCsv } from "@/api/practitioners";

const TEMPLATE_CSV =
  "last_name,first_name,first_initial,rpps,email,phone,birth_date,skills,bio,languages\n" +
  'Martin,Sophie,S,80000000000,sophie.martin@example.com,+33611111111,1985-04-12,PED;PSY,"Urgentiste, 8 ans en SAMU",fr;en\n' +
  "Durand,Pierre,P,80000000001,pierre.durand@example.com,,1978-09-30,,,\n";

export function ImportPractitionersDialog({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [stage, setStage] = useState<"select" | "preview" | "done">("select");

  const reset = () => {
    setFile(null);
    setReport(null);
    setStage("select");
    if (inputRef.current) inputRef.current.value = "";
  };

  const close = () => {
    reset();
    onClose();
  };

  const dryRunMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("no_file");
      return importPractitionersCsv(file, {
        dry_run: true,
        update_existing: updateExisting,
      });
    },
    onSuccess: (r) => {
      setReport(r);
      setStage("preview");
    },
  });

  const commitMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("no_file");
      return importPractitionersCsv(file, {
        dry_run: false,
        update_existing: updateExisting,
      });
    },
    onSuccess: (r) => {
      setReport(r);
      setStage("done");
      onImported();
    },
  });

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cortex-practitioners-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const isBusy = dryRunMutation.isPending || commitMutation.isPending;
  const errors = report?.errors ?? [];
  const errorMsg =
    (dryRunMutation.error as { response?: { data?: { detail?: string } } })?.response
      ?.data?.detail ??
    (commitMutation.error as { response?: { data?: { detail?: string } } })?.response
      ?.data?.detail;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>{t("import.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {stage === "select" && (
            <>
              <Alert severity="info">{t("import.help")}</Alert>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadTemplate}
                >
                  {t("import.downloadTemplate")}
                </Button>
              </Stack>
              <Box
                sx={{
                  p: 3,
                  border: 1,
                  borderStyle: "dashed",
                  borderColor: "divider",
                  borderRadius: 1,
                  textAlign: "center",
                }}
              >
                <input
                  ref={inputRef}
                  type="file"
                  hidden
                  aria-label="Select CSV file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    setFile(f);
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => inputRef.current?.click()}
                >
                  {t("import.pickFile")}
                </Button>
                {file && (
                  <Typography variant="body2" sx={{ mt: 1.5 }}>
                    {file.name} — {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                )}
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                  />
                }
                label={t("import.updateExisting")}
              />
              {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
            </>
          )}

          {(stage === "preview" || stage === "done") && report && (
            <>
              <Alert severity={stage === "done" ? "success" : "info"}>
                {stage === "done"
                  ? t("import.committedNotice", {
                      created: report.created,
                      updated: report.updated,
                      skipped: report.skipped,
                    })
                  : t("import.dryRunNotice", {
                      created: report.created,
                      updated: report.updated,
                      skipped: report.skipped,
                    })}
              </Alert>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip color="primary" label={`${t("import.processed")}: ${report.rows_processed}`} />
                <Chip color="success" label={`${t("import.created")}: ${report.created}`} />
                <Chip color="info" label={`${t("import.updated")}: ${report.updated}`} />
                <Chip color="default" label={`${t("import.skipped")}: ${report.skipped}`} />
                <Chip
                  color={errors.length ? "error" : "default"}
                  label={`${t("import.errors")}: ${errors.length}`}
                />
              </Stack>

              {errors.length > 0 && (
                <Box sx={{ maxHeight: 300, overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("import.col.line")}</TableCell>
                        <TableCell>{t("import.col.field")}</TableCell>
                        <TableCell>{t("import.col.detail")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {errors.map((e, i) => (
                        <TableRow key={i}>
                          <TableCell>{e.line}</TableCell>
                          <TableCell>{e.field ?? "—"}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                            {e.detail}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={isBusy}>
          {stage === "done" ? t("common.close") : t("common.cancel")}
        </Button>
        {stage === "select" && (
          <Button
            variant="contained"
            onClick={() => dryRunMutation.mutate()}
            disabled={!file || isBusy}
            startIcon={isBusy ? <CircularProgress size={16} /> : undefined}
          >
            {t("import.previewCta")}
          </Button>
        )}
        {stage === "preview" && (
          <>
            <Button onClick={() => setStage("select")} disabled={isBusy}>
              {t("common.back")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => commitMutation.mutate()}
              disabled={isBusy}
            >
              {t("import.commitCta")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
